import * as Sentry from "@sentry/react";
import { doc, runTransaction, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db, functions, httpsCallable } from "../firebase";
import {
  identifyTikTokUser,
  trackTikTokCompleteRegistration,
} from "./tiktokPixel";
import {
  identifyRedditUser,
  trackRedditPurchase,
  trackRedditSignUp,
} from "./redditPixel";

export const META_BROWSER_PIXEL_ID = "1348511842435361";
export const META_PIXEL_ID = META_BROWSER_PIXEL_ID;

// This legacy browser pixel powers the active old-account BTS campaign and is
// the source of truth for Meta optimization during this funnel.
export const META_LEGACY_BTS_BROWSER_PIXEL_ID = META_BROWSER_PIXEL_ID;

// The business CAPI pixel is a separate dataset. Do not compare it with the
// legacy browser pixel / old-account Ads Manager results as if they match.
export const META_BUSINESS_CAPI_PIXEL_ID = "1841174409784629";
export const META_CAPI_REGISTRATION_ENABLED = false;

const ATTRIBUTION_STORAGE_KEY = "llp_ad_attribution";
const ATTRIBUTION_VERSION = 2;
const CHECKOUT_INTENT_KEY = "llp_checkout_intent";
const CHECKOUT_BILLING_PERIOD_KEY = "llp_checkout_billing_period";
const SUBSCRIPTION_BROWSER_TRACKED_PREFIX = "llp_subscription_browser_";

/** Set only on brand-new usersNew docs at creation time. Missing ≠ eligible. */
export const META_REGISTRATION_TRACKING_STATUS_ELIGIBLE = "eligible";
/** Atomic claim won; browser events may be in flight. */
export const META_REGISTRATION_TRACKING_STATUS_CLAIMED = "claimed";
/** Legacy in-progress status; treated as already claimed (never re-fire). */
export const META_REGISTRATION_TRACKING_STATUS_PENDING = "pending";
export const META_REGISTRATION_TRACKING_STATUS_FIRED = "fired";

const META_REGISTRATION_DUPLICATE_SKIP_REASONS = new Set([
  "already_fired",
  "already_claimed",
  "in_flight",
]);

/** Same-tab guard against concurrent trackRegistrationSuccess calls. */
const metaRegistrationEmitInFlight = new Set();

const DEFAULT_SUBSCRIPTION_VALUE = 4.99;
const DEFAULT_ANNUAL_SUBSCRIPTION_VALUE = 39.99;

const VIEW_CONTENT_ROUTES = {
  "/": { content_name: "Homepage", content_category: "landing" },
  "/pricing": { content_name: "Pricing", content_category: "subscription" },
  "/show-pricing": { content_name: "Pricing", content_category: "subscription" },
  "/show": { content_name: "Pricing", content_category: "subscription" },
  "/auth/register": { content_name: "Register", content_category: "signup" },
  "/contact": { content_name: "Contact", content_category: "lead" },
  "/for-administrators": { content_name: "For Administrators", content_category: "landing" },
  "/ambassadors": { content_name: "Ambassador Program", content_category: "landing" },
  "/why-lesson-launchpad": { content_name: "Why Lesson Launchpad", content_category: "landing" },
  "/blog": { content_name: "Blog", content_category: "content" },
};

export async function persistMetaSubscriptionCheckoutIntent({
  uid,
  billingPeriod = "monthly",
  monthlyPrice = DEFAULT_SUBSCRIPTION_VALUE,
  yearlyPrice = DEFAULT_ANNUAL_SUBSCRIPTION_VALUE,
  currency = "USD",
} = {}) {
  if (!uid) {
    return null;
  }

  const normalizedBillingPeriod = normalizeBillingPeriod(billingPeriod);
  const resolvedValue = resolveSubscriptionValue({
    billingPeriod: normalizedBillingPeriod,
    value: normalizedBillingPeriod === "annual" ? yearlyPrice : monthlyPrice,
  });
  const subscribeEventId = createMetaEventId("subscribe");
  const tracking = {
    subscribeEventId,
    purchaseEventId: `${subscribeEventId}-purchase`,
    billingPeriod: normalizedBillingPeriod,
    value: resolvedValue,
    currency,
  };

  markMetaCheckoutIntent(uid, normalizedBillingPeriod);

  try {
    await setDoc(
      doc(db, "usersNew", uid),
      { metaSubscriptionTracking: tracking },
      { merge: true }
    );
  } catch (error) {
    console.warn("Failed to persist Meta subscription checkout intent:", error);
  }

  return tracking;
}

export function markMetaCheckoutIntent(uid, billingPeriod = "monthly") {
  if (!uid || typeof sessionStorage === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(CHECKOUT_INTENT_KEY, uid);
    sessionStorage.setItem(
      CHECKOUT_BILLING_PERIOD_KEY,
      normalizeBillingPeriod(billingPeriod)
    );
  } catch {
    // Ignore storage failures in private browsing modes.
  }
}

export function getMetaCheckoutBillingPeriod() {
  if (typeof sessionStorage === "undefined") {
    return "monthly";
  }

  try {
    return normalizeBillingPeriod(sessionStorage.getItem(CHECKOUT_BILLING_PERIOD_KEY));
  } catch {
    return "monthly";
  }
}

export function hasMetaCheckoutIntent(uid) {
  if (!uid || typeof sessionStorage === "undefined") {
    return false;
  }

  try {
    return sessionStorage.getItem(CHECKOUT_INTENT_KEY) === uid;
  } catch {
    return false;
  }
}

export function clearMetaCheckoutIntent() {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  try {
    sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
    sessionStorage.removeItem(CHECKOUT_BILLING_PERIOD_KEY);
  } catch {
    // Ignore storage failures in private browsing modes.
  }
}

export function isSchoolLicensedPremiumUser(user = {}) {
  const licenseType = String(user.schoolLicenseType || "").toLowerCase();
  if (["site", "multi-seat"].includes(licenseType)) {
    return true;
  }

  const tags = Array.isArray(user.tags) ? user.tags : [];
  return tags.some((tag) => ["School Site License", "School Multi-Seat License"].includes(tag));
}

function hasTrackedSubscriptionInBrowser(uid) {
  if (!uid || typeof sessionStorage === "undefined") {
    return false;
  }

  try {
    return sessionStorage.getItem(`${SUBSCRIPTION_BROWSER_TRACKED_PREFIX}${uid}`) === "1";
  } catch {
    return false;
  }
}

function markSubscriptionTrackedInBrowser(uid) {
  if (!uid || typeof sessionStorage === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(`${SUBSCRIPTION_BROWSER_TRACKED_PREFIX}${uid}`, "1");
  } catch {
    // Ignore storage failures in private browsing modes.
  }
}

function normalizeBillingPeriod(value) {
  return String(value || "monthly").toLowerCase() === "annual" ? "annual" : "monthly";
}

function resolveSubscriptionValue({ billingPeriod, value } = {}) {
  const normalizedBillingPeriod = normalizeBillingPeriod(billingPeriod);
  const parsedValue = Number(value);

  if (Number.isFinite(parsedValue) && parsedValue > 0) {
    return parsedValue;
  }

  return normalizedBillingPeriod === "annual"
    ? DEFAULT_ANNUAL_SUBSCRIPTION_VALUE
    : DEFAULT_SUBSCRIPTION_VALUE;
}

function buildSubscriptionEventPayload({ billingPeriod, value, currency = "USD" } = {}) {
  const normalizedBillingPeriod = normalizeBillingPeriod(billingPeriod);
  const resolvedValue = resolveSubscriptionValue({ billingPeriod: normalizedBillingPeriod, value });
  const contentName = normalizedBillingPeriod === "annual" ? "Annual Plan" : "Monthly Plan";

  return {
    content_name: contentName,
    content_category: "subscription",
    value: resolvedValue,
    currency,
    predicted_ltv: normalizedBillingPeriod === "annual" ? resolvedValue : resolvedValue * 12,
    ...getAttributionCustomData(),
  };
}

const CHECKOUT_ROUTES = new Set(["/pricing", "/show-pricing", "/show"]);

function getCookie(name) {
  if (typeof document === "undefined") {
    return "";
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[$()*+.?[\\\]^{|}-]/g, "\\$&")}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function buildFbcFromAttribution(attribution = getStoredAttribution()) {
  const existingFbc = getCookie("_fbc");
  if (existingFbc) {
    return existingFbc;
  }

  const fbclid = attribution?.fbclid;
  if (!fbclid) {
    return "";
  }

  return `fb.1.${Date.now()}.${fbclid}`;
}

export function getMetaBrowserContext() {
  const attribution = getStoredAttribution();

  return {
    sourceUrl: typeof window !== "undefined" ? window.location.href : "",
    fbp: getCookie("_fbp"),
    fbc: buildFbcFromAttribution(attribution),
    attribution: getAttributionCustomData(attribution),
  };
}

async function sendMetaPixelEventsToServer(payload) {
  if (payload?.eventType === "registration" && !META_CAPI_REGISTRATION_ENABLED) {
    return;
  }

  try {
    const sendEvents = httpsCallable(functions, "sendMetaPixelEvents");
    const browserContext = getMetaBrowserContext();

    await sendEvents({
      ...payload,
      sourceUrl: payload.sourceUrl || browserContext.sourceUrl,
      fbp: browserContext.fbp,
      fbc: browserContext.fbc,
      attribution: {
        ...browserContext.attribution,
        ...(payload.attribution || {}),
      },
    });
  } catch (error) {
    console.warn("Meta Conversions API sync failed:", error);
  }
}

function safeFbq(...args) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq(...args);
  }
}

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function normalizeName(name) {
  return String(name || "").trim().toLowerCase().replace(/[^a-z]/g, "");
}

async function hashValue(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized || !window.crypto?.subtle) {
    return "";
  }

  const buffer = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(normalized)
  );

  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function createMetaEventId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createMetaRegistrationEventIds(uid) {
  const eventId = `registration-${uid}`;
  return {
    eventId,
    trialEventId: `${eventId}-trial`,
  };
}

function logMetaRegistrationTracking({
  uid,
  triggerSource,
  registrationMethod,
  outcome,
  skipReason = null,
  idempotencyResult = null,
} = {}) {
  const payload = {
    eventName: "CompleteRegistration",
    uid: uid || null,
    triggerSource: triggerSource || null,
    registrationMethod: registrationMethod || null,
    outcome: outcome || null,
    skipReason: skipReason || null,
    idempotencyResult: idempotencyResult || null,
    timestamp: new Date().toISOString(),
  };

  console.info("[meta-registration]", payload);

  if (
    outcome === "skipped" &&
    skipReason &&
    META_REGISTRATION_DUPLICATE_SKIP_REASONS.has(skipReason)
  ) {
    try {
      Sentry.captureMessage("Meta CompleteRegistration duplicate attempt skipped", {
        level: "warning",
        tags: {
          feature: "meta_registration",
          skip_reason: skipReason,
        },
        extra: payload,
      });
    } catch {
      // Telemetry must never break registration.
    }
  }
}

/**
 * Atomically claims the right to emit browser CompleteRegistration/StartTrial.
 * Only users explicitly marked eligible at account creation can win the claim.
 * Existing users without a marker are treated as not_eligible (never backfill-fire).
 */
async function claimMetaRegistrationTrackingEmit(uid) {
  const userRef = doc(db, "usersNew", uid);
  const deterministicEventIds = createMetaRegistrationEventIds(uid);

  return runTransaction(db, async (transaction) => {
    const userSnap = await transaction.get(userRef);
    if (!userSnap.exists()) {
      return {
        claimed: false,
        skipReason: "missing_user_doc",
        idempotencyResult: "missing_user_doc",
      };
    }

    const tracking = userSnap.data() || {};
    const status = tracking.metaRegistrationTrackingStatus;

    if (tracking.provisioningIncomplete === true) {
      return {
        claimed: false,
        skipReason: "incomplete_setup",
        idempotencyResult: "incomplete_setup",
      };
    }

    if (status === META_REGISTRATION_TRACKING_STATUS_FIRED) {
      return {
        claimed: false,
        skipReason: "already_fired",
        idempotencyResult: "already_fired",
      };
    }

    if (
      status === META_REGISTRATION_TRACKING_STATUS_CLAIMED ||
      status === META_REGISTRATION_TRACKING_STATUS_PENDING
    ) {
      return {
        claimed: false,
        skipReason: "already_claimed",
        idempotencyResult: status,
      };
    }

    if (status !== META_REGISTRATION_TRACKING_STATUS_ELIGIBLE) {
      return {
        claimed: false,
        skipReason: "not_eligible",
        idempotencyResult: status || "missing_status",
      };
    }

    const eventId = tracking.metaRegistrationEventId || deterministicEventIds.eventId;
    const trialEventId =
      tracking.metaRegistrationTrialEventId || deterministicEventIds.trialEventId;

    transaction.update(userRef, {
      metaRegistrationEventId: eventId,
      metaRegistrationTrialEventId: trialEventId,
      metaRegistrationTrackingStatus: META_REGISTRATION_TRACKING_STATUS_CLAIMED,
      metaRegistrationClaimedAt: serverTimestamp(),
    });

    return {
      claimed: true,
      eventId,
      trialEventId,
      idempotencyResult: "claimed",
    };
  });
}

function getStorageItem(key) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key) || null;
  } catch {
    return null;
  }
}

function setStorageItem(key, value) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Ignore storage failures in private browsing modes.
  }

  try {
    window.sessionStorage?.setItem(key, value);
  } catch {
    // Ignore storage failures in private browsing modes.
  }
}

function getCurrentLandingContext() {
  if (typeof window === "undefined") {
    return {
      landingPagePath: "",
      landingPageUrl: "",
    };
  }

  return {
    landingPagePath: `${window.location.pathname || "/"}${window.location.search || ""}`,
    landingPageUrl: window.location.href || "",
  };
}

function normalizeTouchAttribution(value = {}) {
  if (!value || typeof value !== "object") {
    return null;
  }

  const normalized = {
    utm_source: value.utm_source || "",
    utm_medium: value.utm_medium || "",
    utm_campaign: value.utm_campaign || "",
    utm_campaign_id: value.utm_campaign_id || "",
    utm_adset: value.utm_adset || "",
    utm_adset_id: value.utm_adset_id || "",
    utm_content: value.utm_content || "",
    utm_ad_id: value.utm_ad_id || "",
    utm_term: value.utm_term || "",
    mc_cid: value.mc_cid || "",
    mc_eid: value.mc_eid || "",
    email_campaign_id: value.email_campaign_id || "",
    email_audience_id: value.email_audience_id || "",
    fbclid: value.fbclid || "",
    landingPagePath: value.landingPagePath || "",
    landingPageUrl: value.landingPageUrl || "",
    firstVisitAt: value.firstVisitAt || value.capturedAt || "",
    latestVisitAt: value.latestVisitAt || value.capturedAt || "",
    capturedAt: value.capturedAt || value.firstVisitAt || value.latestVisitAt || "",
  };

  return Object.values(normalized).some(Boolean) ? normalized : null;
}

function normalizeStoredAttributionPayload(value = {}) {
  if (!value || typeof value !== "object") {
    return null;
  }

  if (value.firstTouch || value.latestTouch) {
    const firstTouch = normalizeTouchAttribution(value.firstTouch);
    const latestTouch = normalizeTouchAttribution(value.latestTouch) || firstTouch;

    if (!firstTouch && !latestTouch) {
      return null;
    }

    return {
      version: value.version || ATTRIBUTION_VERSION,
      firstTouch: firstTouch || latestTouch,
      latestTouch: latestTouch || firstTouch,
    };
  }

  const legacyTouch = normalizeTouchAttribution(value);
  if (!legacyTouch) {
    return null;
  }

  return {
    version: ATTRIBUTION_VERSION,
    firstTouch: legacyTouch,
    latestTouch: legacyTouch,
  };
}

export function getStoredAttributionPayload() {
  try {
    const raw = getStorageItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? normalizeStoredAttributionPayload(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

export function getStoredAttribution() {
  return getStoredAttributionPayload()?.latestTouch || null;
}

export function getSignupAttribution() {
  const payload = getStoredAttributionPayload();
  if (!payload?.firstTouch && !payload?.latestTouch) {
    return null;
  }

  return {
    firstTouch: payload.firstTouch || payload.latestTouch,
    latestTouch: payload.latestTouch || payload.firstTouch,
  };
}

export function buildAttributionSummary(attribution = getSignupAttribution()) {
  const firstTouch = attribution?.firstTouch || attribution;
  if (!firstTouch) {
    return "";
  }

  return [
    firstTouch.utm_source,
    firstTouch.utm_campaign,
    firstTouch.mc_cid,
    firstTouch.utm_adset,
    firstTouch.utm_content,
  ].filter(Boolean).join(" / ");
}

export function captureAdAttribution(search = "") {
  const params = new URLSearchParams(search);
  const existingPayload = getStoredAttributionPayload();
  const landingContext = getCurrentLandingContext();
  const incoming = {
    fbclid: params.get("fbclid") || "",
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_campaign_id: params.get("utm_campaign_id") || "",
    utm_adset: params.get("utm_adset") || "",
    utm_adset_id: params.get("utm_adset_id") || "",
    utm_content: params.get("utm_content") || "",
    utm_ad_id: params.get("utm_ad_id") || "",
    utm_term: params.get("utm_term") || "",
    mc_cid: params.get("mc_cid") || "",
    mc_eid: params.get("mc_eid") || "",
    email_campaign_id: params.get("email_campaign_id") || "",
    email_audience_id: params.get("email_audience_id") || "",
  };
  const hasIncomingAttribution = Object.values(incoming).some(Boolean);

  if (!hasIncomingAttribution && existingPayload) {
    return existingPayload.latestTouch;
  }

  if (!hasIncomingAttribution) {
    return null;
  }

  const now = new Date().toISOString();
  const existingLatest = existingPayload?.latestTouch || {};
  const latestTouch = {
    fbclid: incoming.fbclid || existingLatest.fbclid || "",
    utm_source: incoming.utm_source || existingLatest.utm_source || "",
    utm_medium: incoming.utm_medium || existingLatest.utm_medium || "",
    utm_campaign: incoming.utm_campaign || existingLatest.utm_campaign || "",
    utm_campaign_id: incoming.utm_campaign_id || existingLatest.utm_campaign_id || "",
    utm_adset: incoming.utm_adset || existingLatest.utm_adset || "",
    utm_adset_id: incoming.utm_adset_id || existingLatest.utm_adset_id || "",
    utm_content: incoming.utm_content || existingLatest.utm_content || "",
    utm_ad_id: incoming.utm_ad_id || existingLatest.utm_ad_id || "",
    utm_term: incoming.utm_term || existingLatest.utm_term || "",
    mc_cid: incoming.mc_cid || existingLatest.mc_cid || "",
    mc_eid: incoming.mc_eid || existingLatest.mc_eid || "",
    email_campaign_id: incoming.email_campaign_id || existingLatest.email_campaign_id || "",
    email_audience_id: incoming.email_audience_id || existingLatest.email_audience_id || "",
    ...landingContext,
    firstVisitAt: existingLatest.firstVisitAt || existingLatest.capturedAt || now,
    latestVisitAt: now,
    capturedAt: existingLatest.capturedAt || now,
  };

  const payload = {
    version: ATTRIBUTION_VERSION,
    firstTouch: existingPayload?.firstTouch || {
      ...latestTouch,
      firstVisitAt: now,
      latestVisitAt: now,
      capturedAt: now,
    },
    latestTouch,
  };

  setStorageItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));

  return payload.latestTouch;
}

export function isEmailAttributionTouch(touch = {}) {
  const source = String(touch.utm_source || "").toLowerCase();
  const medium = String(touch.utm_medium || "").toLowerCase();
  return Boolean(touch.mc_cid || touch.email_campaign_id)
    || source.includes("mailchimp")
    || source.includes("email")
    || medium.includes("email");
}

export function getEmailAttributionFromStorage() {
  return getStoredAttribution();
}

export function inferLeadSourceFromAttribution(attribution = getStoredAttribution()) {
  if (!attribution) {
    return "";
  }

  const source = String(attribution.utm_source || "").toLowerCase();
  const medium = String(attribution.utm_medium || "").toLowerCase();
  const campaign = String(attribution.utm_campaign || "").toLowerCase();
  const mailchimpCampaign = String(attribution.mc_cid || attribution.email_campaign_id || "").toLowerCase();
  const combined = `${source} ${medium} ${campaign} ${mailchimpCampaign}`;

  if (attribution.fbclid || combined.includes("facebook") || combined.includes("meta")) {
    return "Facebook Ad";
  }
  if (combined.includes("instagram")) {
    return "Instagram Ad";
  }
  if (combined.includes("tiktok")) {
    return "TikTok Ad";
  }
  if (combined.includes("pinterest")) {
    return "Pinterest Ad";
  }
  if (combined.includes("google")) {
    return combined.includes("cpc") || combined.includes("paid") ? "Google Ad" : "Google Search";
  }
  if (combined.includes("mailchimp") || combined.includes("email") || mailchimpCampaign) {
    return "Email";
  }

  return "";
}

export function getAttributionCustomData(attribution = getStoredAttribution()) {
  if (!attribution) {
    return {};
  }

  return {
    fbclid: attribution.fbclid || undefined,
    utm_source: attribution.utm_source || undefined,
    utm_medium: attribution.utm_medium || undefined,
    utm_campaign: attribution.utm_campaign || undefined,
    utm_campaign_id: attribution.utm_campaign_id || undefined,
    utm_adset: attribution.utm_adset || undefined,
    utm_adset_id: attribution.utm_adset_id || undefined,
    utm_content: attribution.utm_content || undefined,
    utm_ad_id: attribution.utm_ad_id || undefined,
    utm_term: attribution.utm_term || undefined,
    mc_cid: attribution.mc_cid || undefined,
    mc_eid: attribution.mc_eid || undefined,
    email_campaign_id: attribution.email_campaign_id || undefined,
    email_audience_id: attribution.email_audience_id || undefined,
  };
}

export async function setMetaAdvancedMatching({ email, firstName, lastName } = {}) {
  const userData = {};

  if (email) {
    userData.em = await hashValue(normalizeEmail(email));
  }
  if (firstName) {
    userData.fn = await hashValue(normalizeName(firstName));
  }
  if (lastName) {
    userData.ln = await hashValue(normalizeName(lastName));
  }

  if (!Object.keys(userData).length) {
    return;
  }

  safeFbq("init", META_PIXEL_ID, userData);
}

export function trackMetaEvent(eventName, params = {}, options = {}) {
  safeFbq("track", eventName, params, options);
}

export function trackMetaCustomEvent(eventName, params = {}, options = {}) {
  safeFbq("trackCustom", eventName, params, options);
}

export function trackReminderRequested() {
  trackMetaCustomEvent("ReminderRequested", {
    ...getAttributionCustomData(),
  });
}

export function trackMetaPageView() {
  safeFbq("track", "PageView");
}

export function trackMetaViewContent({ contentName, contentCategory, value, currency = "USD" } = {}) {
  trackMetaEvent("ViewContent", {
    content_name: contentName,
    content_category: contentCategory,
    value,
    currency,
    ...getAttributionCustomData(),
  });
}

export async function trackMetaLead({ email, name, contentName = "Contact Form" } = {}) {
  const eventId = createMetaEventId("lead");
  const [firstName = "", ...lastParts] = String(name || "").trim().split(/\s+/);
  const lastName = lastParts.join(" ");

  trackMetaEvent(
    "Lead",
    {
      content_name: contentName,
      ...getAttributionCustomData(),
    },
    { eventID: eventId }
  );

  if (email || name) {
    setMetaAdvancedMatching({
      email,
      firstName,
      lastName,
    });
  }

  void sendMetaPixelEventsToServer({
    eventType: "lead",
    leadEventId: eventId,
    email,
    firstName,
    lastName,
    contentName,
  });
}

export function trackViewContentForPath(pathname) {
  const routeConfig = VIEW_CONTENT_ROUTES[pathname];
  if (routeConfig) {
    trackMetaViewContent(routeConfig);

    if (CHECKOUT_ROUTES.has(pathname)) {
      trackMetaEvent("InitiateCheckout", {
        content_name: routeConfig.content_name,
        content_category: routeConfig.content_category,
        currency: "USD",
        ...getAttributionCustomData(),
      });
    }
    return;
  }

  if (pathname.startsWith("/blog/") && pathname !== "/blog/") {
    trackMetaViewContent({
      contentName: "Blog Article",
      contentCategory: "content",
    });
  }
}

export async function trackRegistrationSuccess({
  uid,
  email,
  firstName,
  lastName,
  triggerSource = "unknown",
  registrationMethod = "unknown",
} = {}) {
  if (!uid) {
    logMetaRegistrationTracking({
      triggerSource,
      registrationMethod,
      outcome: "skipped",
      skipReason: "missing_uid",
      idempotencyResult: "missing_uid",
    });
    return { emitted: false, skipReason: "missing_uid" };
  }

  if (metaRegistrationEmitInFlight.has(uid)) {
    logMetaRegistrationTracking({
      uid,
      triggerSource,
      registrationMethod,
      outcome: "skipped",
      skipReason: "in_flight",
      idempotencyResult: "in_flight",
    });
    return { emitted: false, skipReason: "in_flight" };
  }

  metaRegistrationEmitInFlight.add(uid);

  try {
    const claim = await claimMetaRegistrationTrackingEmit(uid);
    if (!claim.claimed) {
      logMetaRegistrationTracking({
        uid,
        triggerSource,
        registrationMethod,
        outcome: "skipped",
        skipReason: claim.skipReason,
        idempotencyResult: claim.idempotencyResult,
      });
      return { emitted: false, skipReason: claim.skipReason };
    }

    await setMetaAdvancedMatching({ email, firstName, lastName });

    // TikTok: identify for EMQ, then fire standard CompleteRegistration on the
    // real signup success path (same once-per-uid gate as Meta). Do not rely on
    // Events Manager click/URL rules for optimization eligibility.
    identifyTikTokUser({ email, externalId: uid });
    trackTikTokCompleteRegistration({ eventId: claim.eventId });
    identifyRedditUser({ email, externalId: uid });
    trackRedditSignUp({
      conversionId: claim.eventId,
      email,
      externalId: uid,
    });

    const payload = {
      status: "trial",
      ...getAttributionCustomData(),
    };

    trackMetaEvent("CompleteRegistration", payload, { eventID: claim.eventId });
    trackMetaEvent(
      "StartTrial",
      {
        ...payload,
        value: 0,
        currency: "USD",
      },
      { eventID: claim.trialEventId }
    );

    await updateDoc(doc(db, "usersNew", uid), {
      metaRegistrationTrackingStatus: META_REGISTRATION_TRACKING_STATUS_FIRED,
      metaRegistrationFiredAt: serverTimestamp(),
      // Legacy alias kept for documents/tools that still read trackedAt.
      metaRegistrationTrackedAt: serverTimestamp(),
    });

    logMetaRegistrationTracking({
      uid,
      triggerSource,
      registrationMethod,
      outcome: "emitted",
      idempotencyResult: "fired",
    });

    if (META_CAPI_REGISTRATION_ENABLED) {
      void sendMetaPixelEventsToServer({
        eventType: "registration",
        registrationEventId: claim.eventId,
        trialEventId: claim.trialEventId,
        email,
        firstName,
        lastName,
        attribution: payload,
      });
    }

    return { emitted: true, eventId: claim.eventId };
  } catch (error) {
    console.warn("Meta registration tracking failed:", error);
    logMetaRegistrationTracking({
      uid,
      triggerSource,
      registrationMethod,
      outcome: "skipped",
      skipReason: "tracking_error",
      idempotencyResult: "tracking_error",
    });
    return { emitted: false, skipReason: "tracking_error" };
  } finally {
    metaRegistrationEmitInFlight.delete(uid);
  }
}

export async function trackSubscriptionSuccess({
  uid,
  email,
  firstName,
  lastName,
  billingPeriod = "monthly",
  value,
  currency = "USD",
  subscribeEventId,
  purchaseEventId,
} = {}) {
  if (!uid || hasTrackedSubscriptionInBrowser(uid)) {
    return;
  }

  const normalizedBillingPeriod = normalizeBillingPeriod(billingPeriod);
  const resolvedSubscribeEventId = subscribeEventId || createMetaEventId("subscribe");
  const resolvedPurchaseEventId = purchaseEventId || `${resolvedSubscribeEventId}-purchase`;
  const payload = buildSubscriptionEventPayload({
    billingPeriod: normalizedBillingPeriod,
    value,
    currency,
  });

  await setMetaAdvancedMatching({ email, firstName, lastName });

  trackMetaEvent("Subscribe", payload, { eventID: resolvedSubscribeEventId });
  trackMetaEvent("Purchase", payload, { eventID: resolvedPurchaseEventId });
  identifyRedditUser({ email, externalId: uid });
  trackRedditPurchase({
    conversionId: resolvedPurchaseEventId,
    value: payload.value,
    currency: payload.currency,
    email,
    externalId: uid,
  });

  markSubscriptionTrackedInBrowser(uid);
  clearMetaCheckoutIntent();

  void sendMetaPixelEventsToServer({
    eventType: "subscription",
    subscribeEventId: resolvedSubscribeEventId,
    purchaseEventId: resolvedPurchaseEventId,
    email,
    firstName,
    lastName,
    billingPeriod: normalizedBillingPeriod,
    value: payload.value,
    currency: payload.currency,
    attribution: getAttributionCustomData(),
  });
}
